import { Flex, Heading, Text, VStack } from '@chakra-ui/react';
import { FC } from 'react';
import { ChecklistItem } from '../utils/buildChecklistItems';
import { folderNameForItem } from '../utils/groupItemsByFolder';
import { ChecklistItemRow } from './ChecklistItemRow';

interface NeedsReviewChecklistProps {
  items: ChecklistItem[];
  /** The active checklist item's id (the item containing the selected tab). */
  activeItemId?: string;
  /**
   * When true, show each standalone row's platform folder as a caption line.
   * Real checklist items already carry their checklist identity as the row
   * title, while standalone documents need their folder context preserved.
   */
  showFolderName?: boolean;
  /** Selects a checklist item by opening its first document tab. */
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * Left-rail "Needs review" section: the items that still need the agent's
 * review, shown directly (no folder grouping) as a flat selectable list with a
 * count badge. On non-SkySlope surfaces each row additionally shows its folder
 * name (see {@link NeedsReviewChecklistProps.showFolderName}). Renders nothing
 * when empty.
 */
export const NeedsReviewChecklist: FC<NeedsReviewChecklistProps> = ({
  items,
  activeItemId,
  showFolderName = false,
  onSelectDocument,
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex align="center" gap={1.5} px={2.5}>
        <Heading
          color="compliance.text.primary"
          fontSize="md"
          fontWeight="semibold"
          size="headline"
        >
          Needs review
        </Heading>
        <Flex
          align="center"
          bg="whiteAlpha.100"
          borderRadius="6px"
          justify="center"
          px={2}
          py={0.5}
        >
          <Text color="compliance.status.warning" fontSize="sm">
            {items.length}
          </Text>
        </Flex>
      </Flex>

      <VStack align="stretch" spacing={1.5}>
        {items.map((item) => (
          <ChecklistItemRow
            key={item.id}
            caption={
              showFolderName && !item.isChecklistItem
                ? folderNameForItem(item)
                : undefined
            }
            isSelected={item.id === activeItemId}
            title={item.title}
            onSelect={() => onSelectDocument(item.docs[0].ingestionDocumentId)}
          />
        ))}
      </VStack>
    </VStack>
  );
};
